import React, { useState, useEffect, useRef } from 'react';
import { ArrowLeft, Brain, Loader2, Check, FileText, Table, Image, Zap, Send, Paperclip, Mic } from 'lucide-react';
import { TASK_CONVERSATIONS } from '../../data/taskConversations';
import { CEO_BRIEFING } from '../../data/ceoBriefing';
import { scaleDelay } from '../../hooks/demoSpeed';

const AGENT_THUMBS = {
  luc: '/Thumbnails/Luc.png',
  marsi: '/Thumbnails/Marsi.png',
  sally: '/Thumbnails/Sally.png',
  stark: '/Thumbnails/Stark.png',
  tali: '/Thumbnails/Tali.png',
  wright: '/Thumbnails/Wright.png',
  buzz: '/Thumbnails/Buzz.png',
  dora: '/Thumbnails/Dora.png',
  finn: '/Thumbnails/Finn.png',
  zira: '/Thumbnails/Ziira.png',
};
const AGENT_NAMES = {
  luc: 'Luc', marsi: 'Marsi', sally: 'Sally', stark: 'Stark', tali: 'Tali',
  wright: 'Wright', buzz: 'Buzz', dora: 'Dora', finn: 'Finn', zira: 'Zira',
};

const ARTIFACT_ICONS = { pdf: FileText, image: Image, spreadsheet: Table };

const ThinkingBubble = ({ text, isDark }) => (
  <div
    className={`ml-[52px] flex items-start gap-3 px-5 py-4 rounded-xl ${isDark ? 'bg-indigo-500/8 border border-indigo-500/10' : 'bg-indigo-50 border border-indigo-200/40'}`}
    style={{ animation: 'taskFadeIn 0.3s ease-out' }}
  >
    <Brain className={`w-4 h-4 mt-0.5 flex-shrink-0 animate-pulse ${isDark ? 'text-indigo-400' : 'text-indigo-500'}`} />
    <div>
      <p className={`text-[12px] font-medium ${isDark ? 'text-indigo-400' : 'text-indigo-500'}`}>Thinking...</p>
      <p className={`text-[11px] mt-0.5 ${isDark ? 'text-white/40' : 'text-slate-500'}`}>{text}</p>
    </div>
  </div>
);

const TypingBubble = ({ isDark, agentThumb }) => (
  <div className="flex items-start gap-4" style={{ animation: 'taskFadeIn 0.3s ease-out' }}>
    {agentThumb && <img src={agentThumb} alt="" className="w-9 h-9 rounded-full object-cover flex-shrink-0 mt-0.5" />}
    <div className="flex gap-1.5 py-2">
      <span className={`w-2 h-2 rounded-full ${isDark ? 'bg-white/30' : 'bg-slate-400'} animate-bounce`} style={{ animationDelay: '0ms' }} />
      <span className={`w-2 h-2 rounded-full ${isDark ? 'bg-white/30' : 'bg-slate-400'} animate-bounce`} style={{ animationDelay: '150ms' }} />
      <span className={`w-2 h-2 rounded-full ${isDark ? 'bg-white/30' : 'bg-slate-400'} animate-bounce`} style={{ animationDelay: '300ms' }} />
    </div>
  </div>
);

const V4TaskExecution = ({ isDark, taskId, onBack, onComplete, simState, taskData: taskDataProp }) => {
  const conversation = TASK_CONVERSATIONS[taskId];
  // Look up task data: prop override > CEO mustDo > followUp tasks > fallback
  const taskData = taskDataProp
    || CEO_BRIEFING.priorities.mustDo.find(t => t.id === taskId)
    || simState?.followUpTasks?.find(t => t.id === taskId)
    || { title: conversation?.title || 'Task', agent: conversation?.agent || 'luc', description: '', agentNote: '' };

  const [messages, setMessages] = useState([]);
  const [phase, setPhase] = useState('idle');
  const [thinkingText, setThinkingText] = useState('');
  const [isCompleted, setIsCompleted] = useState(false);
  const [taskChatInput, setTaskChatInput] = useState('');
  const handleTaskChatSend = () => {
    if (!taskChatInput.trim() || !isCompleted) return;
    const text = taskChatInput.trim();
    setMessages((prev) => [...prev, { role: 'user', text }, { role: 'agent', text: 'Understood. I\'ll handle that and flag you once done.' }]);
    setTaskChatInput('');
  };
  const stepRef = useRef(0);
  const timerRefs = useRef([]);
  const completedRef = useRef(false);
  const messagesEndRef = useRef(null);

  const textPrimary = isDark ? 'text-white' : 'text-slate-800';
  const textSecondary = isDark ? 'text-white/50' : 'text-slate-500';
  const divider = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';
  const cardBg = isDark ? 'bg-[#0d1b2a]' : 'bg-[#f0ece4]';

  const agentId = conversation?.agent || 'luc';
  const agentThumb = AGENT_THUMBS[agentId];

  const clearTimers = () => {
    timerRefs.current.forEach(clearTimeout);
    timerRefs.current = [];
  };

  const addTimer = (callback, delay) => {
    const id = setTimeout(callback, scaleDelay(delay));
    timerRefs.current.push(id);
  };

  const playStep = () => {
    if (!conversation || stepRef.current >= conversation.steps.length) {
      setPhase('done');
      return;
    }

    const step = conversation.steps[stepRef.current];

    if (step.role === 'user') {
      addTimer(() => {
        setMessages((prev) => [...prev, { role: 'user', text: step.text }]);
        stepRef.current++;
        addTimer(() => playStep(), 600);
      }, 1200);
    } else if (step.role === 'agent') {
      if (step.thinking) {
        setPhase('thinking');
        setThinkingText(step.thinking);
        addTimer(() => {
          setPhase('typing');
          addTimer(() => {
            setMessages((prev) => [...prev, { role: 'agent', text: step.text }]);
            setPhase('idle');
            if (step.completes && !completedRef.current) {
              completedRef.current = true;
              setIsCompleted(true);
              onComplete?.();
            }
            stepRef.current++;
            addTimer(() => playStep(), 800);
          }, step.delay || 1500);
        }, 1800);
      } else {
        setPhase('typing');
        addTimer(() => {
          setMessages((prev) => [...prev, { role: 'agent', text: step.text }]);
          setPhase('idle');
          if (step.completes && !completedRef.current) {
            completedRef.current = true;
            setIsCompleted(true);
            onComplete?.();
          }
          stepRef.current++;
          addTimer(() => playStep(), 800);
        }, step.delay || 1500);
      }
    }
  };

  useEffect(() => {
    if (conversation) {
      clearTimers();
      stepRef.current = 0;
      completedRef.current = false;
      setMessages([]);
      setPhase('idle');
      setIsCompleted(false);
      addTimer(() => playStep(), 600);
    }
    return clearTimers;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, phase]);

  if (!conversation || !taskData) return null;

  return (
    <div className="flex flex-col h-full" data-testid="task-execution-view">
      <style>{`
        @keyframes taskFadeIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes taskSlideIn { from { opacity: 0; transform: translateX(-10px); } to { opacity: 1; transform: translateX(0); } }
      `}</style>

      {/* Header */}
      <div className={`flex items-center gap-3 px-5 py-3.5 border-b ${divider} flex-shrink-0`}>
        <button
          onClick={onBack}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${isDark ? 'hover:bg-white/[0.06] text-white/60 hover:text-white' : 'hover:bg-black/[0.04] text-slate-500 hover:text-slate-800'}`}
          data-testid="task-execution-back"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          Back to Briefing
        </button>
        <div className={`h-4 w-px ${divider}`} />
        {agentThumb && <img src={agentThumb} alt="" className="w-6 h-6 rounded-full object-cover" />}
        <div className="flex-1 min-w-0">
          <p className={`text-sm font-semibold ${textPrimary}`}>{taskData.title}</p>
          <p className={`text-[10px] ${textSecondary}`}>Working with {AGENT_NAMES[agentId] || 'Luc'}</p>
        </div>
        {isCompleted ? (
          <span className="flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-medium bg-emerald-500/15 text-emerald-400" data-testid="task-execution-completed">
            <Check className="w-3.5 h-3.5" /> Task Completed
          </span>
        ) : (
          <span className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-medium ${isDark ? 'bg-indigo-500/15 text-indigo-400' : 'bg-indigo-500/10 text-indigo-600'}`}>
            <Loader2 className="w-3.5 h-3.5 animate-spin" /> In Progress
          </span>
        )}
      </div>

      {/* Content — two-column layout */}
      <div className="flex-1 flex min-h-0">
        {/* Left: Task Context */}
        <div className={`w-[340px] flex-shrink-0 border-r ${divider} p-5 overflow-y-auto`}>
          <div className={`${cardBg} border ${divider} rounded-xl p-5 space-y-4`}>
            <div className="flex items-center gap-3">
              <img src={AGENT_THUMBS[taskData.agent]} alt="" className="w-7 h-7 rounded-full object-cover" />
              <div>
                <p className={`text-[13px] font-medium ${textPrimary}`}>{taskData.title}</p>
                <p className={`text-[11px] ${textSecondary}`}>{taskData.agentNote}</p>
              </div>
            </div>

            <p className={`text-[12px] leading-[1.7] ${isDark ? 'text-white/60' : 'text-slate-600'}`}>{taskData.description}</p>

            {taskData.expanded?.summary && (
              <div className={`pt-4 border-t ${divider}`}>
                <p className={`text-[11px] font-medium mb-1.5 ${textSecondary}`}>Summary</p>
                <p className={`text-[12px] leading-[1.7] ${isDark ? 'text-white/60' : 'text-slate-600'}`}>{taskData.expanded.summary}</p>
              </div>
            )}

            {taskData.expanded?.details && (
              <div className={`pt-4 border-t ${divider}`}>
                <p className={`text-[11px] font-medium mb-2 ${textSecondary}`}>Key Data</p>
                <div className={`rounded-lg overflow-hidden border ${divider}`}>
                  {taskData.expanded.details.map((d, i) => (
                    <div key={i} className={`flex items-center justify-between px-3 py-2 text-[11px] ${i % 2 === 0 ? (isDark ? 'bg-white/[0.02]' : 'bg-black/[0.02]') : ''}`}>
                      <span className={textSecondary}>{d.label}</span>
                      <span className={`font-medium ${textPrimary}`}>{d.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {taskData.expanded?.artifacts && (
              <div className={`pt-4 border-t ${divider}`}>
                <p className={`text-[11px] font-medium mb-2 ${textSecondary}`}>Artifacts</p>
                <div className="space-y-2">
                  {taskData.expanded.artifacts.map((a, i) => {
                    const Icon = ARTIFACT_ICONS[a.type] || FileText;
                    return (
                      <div key={i} className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-[11px] ${isDark ? 'bg-white/[0.04] text-white/60 border border-white/[0.06]' : 'bg-black/[0.03] text-slate-600 border border-black/[0.05]'}`}>
                        <Icon className="w-3 h-3 flex-shrink-0" />
                        {a.name}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {taskData.expanded?.thinking && (
              <div className={`pt-4 border-t ${divider}`}>
                <div className="flex items-center gap-1.5 mb-1.5">
                  <Brain className={`w-3.5 h-3.5 ${isDark ? 'text-indigo-400' : 'text-indigo-500'}`} />
                  <p className={`text-[11px] font-medium ${isDark ? 'text-indigo-400' : 'text-indigo-500'}`}>Thinking Trail</p>
                </div>
                <p className={`text-[10px] leading-relaxed ${isDark ? 'text-white/40' : 'text-slate-500'}`}>{taskData.expanded.thinking}</p>
              </div>
            )}
          </div>
        </div>

        {/* Right: Chat Conversation */}
        <div className="flex-1 flex flex-col min-h-0">
          <div className="flex-1 overflow-y-auto px-10 py-8">
            {messages.map((msg, i) => (
              <div key={i} style={{ marginTop: i === 0 ? 0 : 48, animation: 'taskFadeIn 0.35s ease-out' }}>
                {msg.role === 'user' ? (
                  <div className="flex justify-end">
                    <div className={`max-w-[70%] px-5 py-3.5 rounded-2xl rounded-br-md text-[14px] leading-[1.8] ${isDark ? 'bg-indigo-500/20 text-white' : 'bg-indigo-100 border border-indigo-200/80 text-slate-800'}`}>
                      {msg.text}
                    </div>
                  </div>
                ) : (
                  <div className="flex items-start gap-4">
                    {agentThumb && <img src={agentThumb} alt="" className="w-9 h-9 rounded-full object-cover flex-shrink-0 mt-0.5" />}
                    <div className={`max-w-[85%] text-[14px] leading-[1.8] ${isDark ? 'text-white/85' : 'text-slate-700'}`}>
                      {msg.text.split('\n').filter(Boolean).map((p, j, arr) => (
                        <p key={j} style={{ marginBottom: j < arr.length - 1 ? 20 : 0 }}>{p}</p>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}

            {phase === 'thinking' && <div style={{ marginTop: 48 }}><ThinkingBubble text={thinkingText} isDark={isDark} /></div>}
            {phase === 'typing' && <div style={{ marginTop: 48 }}><TypingBubble isDark={isDark} agentThumb={agentThumb} /></div>}

            <div ref={messagesEndRef} />
          </div>

          {/* Chat input — always visible */}
          <div className="flex justify-center pb-4 px-4 pt-2 flex-shrink-0">
            <div className="w-[80%]">
              <div className={`${cardBg} border ${divider} rounded-2xl flex flex-col ${!isCompleted ? 'opacity-70' : ''}`}>
                <div className="px-4 pt-3 pb-2">
                  <textarea
                    value={taskChatInput}
                    onChange={(e) => setTaskChatInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleTaskChatSend(); } }}
                    placeholder={isCompleted ? `Message ${AGENT_NAMES[agentId] || 'Luc'}...` : `${AGENT_NAMES[agentId] || 'Luc'} is working through this with you...`}
                    rows={1}
                    disabled={!isCompleted}
                    className={`w-full bg-transparent resize-none outline-none text-sm leading-5 ${textPrimary} placeholder:${textSecondary}`}
                    style={{ height: 'auto', maxHeight: '200px' }}
                    data-testid="task-execution-chat-input"
                  />
                </div>
                <div className="flex items-center justify-between px-3 pb-3">
                  <div className="flex items-center gap-1">
                    <div className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg ${isDark ? 'bg-white/[0.08] ring-1 ring-indigo-500/40' : 'bg-indigo-50 ring-1 ring-indigo-300/60'}`}>
                      {agentThumb && <img src={agentThumb} alt="" className="w-5 h-5 rounded-full object-cover" />}
                      <span className={`text-xs font-medium ${textPrimary}`}>{AGENT_NAMES[agentId] || 'Luc'}</span>
                    </div>
                    <button className={`p-1.5 rounded-lg transition-colors ${isDark ? 'hover:bg-white/[0.06]' : 'hover:bg-black/[0.04]'}`}>
                      <Paperclip className={`w-3.5 h-3.5 ${textSecondary}`} />
                    </button>
                  </div>
                  <div className="flex items-center gap-1.5">
                    {isCompleted ? (
                      <button
                        onClick={onBack}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-gradient-to-r from-indigo-500 to-purple-600 text-white"
                        data-testid="task-execution-return"
                      >
                        Return to Briefing
                      </button>
                    ) : (
                      <span className={`text-[11px] flex items-center gap-1.5 ${textSecondary}`}>
                        <Loader2 className="w-3 h-3 animate-spin" /> Working...
                      </span>
                    )}
                    <button className={`w-8 h-8 rounded-xl flex items-center justify-center transition-colors ${isDark ? 'hover:bg-white/[0.06]' : 'hover:bg-black/[0.04]'}`}>
                      <Mic className={`w-4 h-4 ${textSecondary}`} />
                    </button>
                    <button
                      onClick={handleTaskChatSend}
                      disabled={!isCompleted || !taskChatInput.trim()}
                      className={`w-8 h-8 rounded-xl flex items-center justify-center transition-colors ${isCompleted && taskChatInput.trim() ? 'bg-gradient-to-br from-indigo-500 to-purple-600' : (isDark ? 'bg-white/[0.06]' : 'bg-black/[0.04]')}`}
                      data-testid="task-execution-chat-send"
                    >
                      <Send className={`w-3.5 h-3.5 ${isCompleted && taskChatInput.trim() ? 'text-white' : textSecondary}`} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default V4TaskExecution;
